import { isNil } from '../../../utils/unit';
import { ContextBindingController } from '../ContextBindingController';
/**
 * Binds CSS variables to a context.
 */
export class ContextCssVarsController extends ContextBindingController {
    constructor() {
        super(...arguments);
        this._transformers = new Map();
    }
    /**
     * Binds a context to a CSS variable on the current `ref` element. By binds we mean as the
     * context value changes it will update the given CSS custom property on the current `ref`
     * element.
     *
     * @param context The context to bind to.
     * @param varName The name of the CSS custom property to bind the context to. This is will
     * result in `--${varName}`.
     * @param transformer An optional transformer to transform the context value before it is
     * stringified and applied as the CSS custom property value. Returning `null` or `undefined`
     * will remove the custom property.
     */
    bind(context, varName, transformer) {
        const transform = transformer
            ? (_, value) => transformer(value)
            : this._transformVarValue;
        this._transformers.set(varName, transform);
        return super.bind(context, varName);
    }
    _handleBindToContext() {
        // no-op
    }
    _handleBindingUpdate(consumer, varName) {
        if (this._ref instanceof HTMLElement) {
            const name = `--${this._transformVarName(varName)}`;
            const transform = this._transformers.get(varName);
            const value = transform(varName, consumer.value);
            if (isNil(value)) {
                this._ref.style.removeProperty(name);
            }
            else {
                this._ref.style.setProperty(name, String(value));
            }
        }
    }
    _handleUnbindFromContext(_, varName) {
        if (this._ref instanceof HTMLElement) {
            const name = `--${this._transformVarName(varName)}`;
            this._ref.style.removeProperty(name);
        }
    }
    /**
     * Override to transform CSS variable names.
     */
    _transformVarName(varName) {
        return varName;
    }
    /**
     * Override to transform context values to be used as CSS property values. Returning `null`
     * or `undefined` will remove the CSS property. This is provided as the default transformer when
     * one isn't provided in the `bind()` method call..
     */
    _transformVarValue(varName, value) {
        if (typeof value === 'number' && isNaN(value))
            return 0;
        return value;
    }
}
